import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'

import { useAppSelector, useAppDispatch } from '~/redux/hooks'
import curriculumSanity from '~/shared/sanity/curriculum'
import { selectSelectedGrade } from '~/redux/slices/dashboard'
import {
  selectGradeListByCountry,
  selectGradeTitle,
  setGradeListByCountryActionCreator,
} from '~/curriculum/slice/curriculum.slice'
import { extractCountryCodeAndGradeIdFromRouterPath } from '~/curriculum/utils/extractQueryParams'
import { useGetUserChosenCurriculumCountryCode } from '~/curriculum/hooks/useGetCurriculumCountryCode'

const useGradeTitleFromLevel = (): string | undefined => {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const gradeTitleFromSlice = useAppSelector(selectGradeTitle)

  const { countryCode: urlCountryCode, gradeLevel: urlGradeLevel } = extractCountryCodeAndGradeIdFromRouterPath(router)
  // Extract gradeLevel and countryCode from URL using existing function
  const userChosenCountryCode = useGetUserChosenCurriculumCountryCode()
  const countryCode = urlCountryCode ? urlCountryCode : userChosenCountryCode

  const lastSelectedGrade = useAppSelector(selectSelectedGrade)
  const gradeLevel = urlGradeLevel ? urlGradeLevel : lastSelectedGrade

  const gradeList = useAppSelector(selectGradeListByCountry)
  const [isLoading, setIsLoading] = useState(false)
  const [fallbackGradeList, setFallbackGradeList] = useState<typeof gradeList>([])

  // Fetch grade list if not available in Redux state
  useEffect(() => {
    const fetchGradeList = async (): Promise<void> => {
      if (gradeList.length === 0 && countryCode && !isLoading) {
        setIsLoading(true)
        try {
          const fetchedGradeList = await curriculumSanity.getCurriculumGradeListByCountry(countryCode, 'v1')
          // Update Redux store for future use
          dispatch(setGradeListByCountryActionCreator(fetchedGradeList))
          setFallbackGradeList(fetchedGradeList)
        } catch (error) {
          setIsLoading(false)
        } finally {
          setIsLoading(false)
        }
      }
    }

    fetchGradeList()
  }, [gradeList.length, countryCode, dispatch, isLoading])

  // Use Redux grade list if available, otherwise use fallback
  const currentGradeList = gradeList.length > 0 ? gradeList : fallbackGradeList

  // Find grade by the provided grade level, or fallback to last selected grade
  const gradeDetails = currentGradeList.find((grade) => grade.gradeLevel === Number(gradeLevel))

  // Use grade title from grade list if available, otherwise fallback to selectGradeTitle
  return gradeDetails?.title || gradeTitleFromSlice
}

export default useGradeTitleFromLevel
